/**
 * Configuração de integração (D11, §6, §12.1).
 *
 * ── O que estes schemas deliberadamente NÃO têm ───────────────────────────
 *
 * Campo de credencial na LEITURA. Nem o valor, nem a referência: a referência
 * não é o segredo, mas é o endereço dele, e devolvê-la ensinaria a tela a
 * exibir uma string que parece credencial. O que a interface precisa saber é se
 * está configurada, e isso é um booleano.
 */

import { z } from 'zod';

export const IntegracaoSchema = z
  .object({
    provider: z.string(),
    /** Nome de exibição — "Chatwoot", e não `chatwoot`. */
    rotulo: z.string(),
    base_url: z.string(),
    conta_externa: z.string(),
    status: z.string(),
    /** Booleano, e nunca o valor. Ver a nota no topo. */
    credencial_configurada: z.boolean(),
    provider_version: z.string().nullable(),
    schema_sincronizado_em: z.string().datetime().nullable(),
    criado_em: z.string().datetime(),
  })
  .describe('Conexão de engine, sem segredo');

export const ListaDeIntegracoesSchema = z.object({ itens: z.array(IntegracaoSchema) });

/**
 * Um canal de WhatsApp conectado.
 *
 * ── Por que é rota própria, e não campo da integração ─────────────────────
 *
 * Porque a integração é a conexão com o MOTOR — uma por revenda — e o canal é
 * um número. Uma revenda tem um motor e pode ter vários números; pendurar a
 * lista dentro da integração faria a tela reler a configuração inteira do
 * motor para descobrir que entrou um número novo.
 *
 * ── Sem token, sem `phone_number_id`, sem WABA ────────────────────────────
 *
 * A tela precisa responder "qual número está conectado?", e é só isso que vai.
 * O `phone_number_id` e o id da conta comercial são identificadores da Meta:
 * não são segredo, mas também não dizem nada a quem lê, e ensinariam a tela a
 * exibir string de engine — o que a §3.2 não quer.
 */
/**
 * As duas mensagens que o canal manda SOZINHO.
 *
 * ── Por que só estas duas ─────────────────────────────────────────────────
 *
 * O motor de conversa oferece quatro formas de mensagem automática: saudação,
 * fora de expediente, regra por gatilho e campanha. Estas duas são as únicas
 * que REAGEM a uma mensagem do cliente — e é isso que as torna seguras no
 * WhatsApp.
 *
 * A janela da Meta é de 24 horas a partir da última mensagem recebida. Quem
 * responde a quem acabou de escrever está sempre dentro dela. Já uma regra que
 * o negócio dispara, ou uma campanha, cai fora — e fora da janela só passa
 * template aprovado, que leva até 24 horas para ser revisado. Oferecer texto
 * livre ali seria oferecer uma mensagem que a Meta recusa com `131047`.
 *
 * ── O texto pode estar VAZIO com a chave ligada ───────────────────────────
 *
 * O motor trata os dois como condições separadas — ele só envia se estiver
 * ligada E o texto existir. A tela precisa saber disso para explicar por que
 * uma saudação "ligada" não chega.
 */
export const MensagemAutomaticaSchema = z
  .object({
    ligada: z.boolean(),
    /**
     * 10 000 é o teto do motor (`Limits::GREETING_MESSAGE_MAX_LENGTH` e
     * `OUT_OF_OFFICE_MESSAGE_MAX_LENGTH`, lidos da instância v4.16.2).
     *
     * Repetido aqui de propósito: sem o teto no contrato, quem escrevesse
     * demais só descobriria no 422 do motor, com o texto já perdido.
     */
    texto: z.string().max(10_000),
  })
  .strict();

export const MensagensAutomaticasSchema = z
  .object({
    /** Responde à PRIMEIRA mensagem de cada contato. */
    saudacao: MensagemAutomaticaSchema,
    /**
     * Responde fora do expediente, uma vez por dia.
     *
     * `ligada` reflete o `working_hours_enabled` do motor: sem horário
     * configurado lá, o motor considera o canal sempre aberto e a mensagem
     * nunca sai — ligada ou não.
     */
    fora_de_expediente: MensagemAutomaticaSchema,
  })
  .strict();

export const CanalDeWhatsAppSchema = z
  .object({
    /** O id da caixa no motor. É o que a transferência e o escopo usam. */
    referencia_externa: z.string(),
    nome: z.string(),
    /** Em formato internacional, como o motor guarda: `+5519...`. */
    telefone: z.string(),
    mensagens_automaticas: MensagensAutomaticasSchema,
  })
  .strict();

/**
 * `PATCH .../channels/whatsapp/{id}/mensagens-automaticas`.
 *
 * Os dois blocos são opcionais, e ausente significa "não mexa": mandar o
 * objeto inteiro a cada salvamento apagaria a saudação de quem só quis trocar
 * o texto de fora de expediente.
 */
export const AlterarMensagensAutomaticasSchema = z
  .object({
    saudacao: MensagemAutomaticaSchema.optional(),
    fora_de_expediente: MensagemAutomaticaSchema.optional(),
  })
  .strict();

export type MensagemAutomatica = z.infer<typeof MensagemAutomaticaSchema>;
export type MensagensAutomaticas = z.infer<typeof MensagensAutomaticasSchema>;
export type AlterarMensagensAutomaticas = z.infer<typeof AlterarMensagensAutomaticasSchema>;

/* ── Modelos de mensagem (H19b) ──────────────────────────────────────────── */

/**
 * O nome do modelo, na regra da META e não na nossa.
 *
 * Minúsculas, dígitos e sublinhado. A recusa por nome inválido volta da Meta
 * como erro genérico de parâmetro, e quem está cadastrando fica olhando para o
 * texto da mensagem — que está certo.
 */
export const NomeDeModeloSchema = z
  .string()
  .min(1)
  .max(512)
  .regex(/^[a-z0-9_]+$/, 'Use só minúsculas, números e sublinhado: `retomada_de_atendimento`.');

/**
 * A categoria, sem `AUTHENTICATION`.
 *
 * Autenticação é para código de uso único, tem regras próprias de formato e a
 * Meta a trata como outro produto. O catálogo de envio já a filtra
 * (`capabilidades-templates.ts`), então oferecê-la aqui seria deixar a revenda
 * cadastrar um modelo que ela nunca veria na tela de atendimento.
 */
export const CategoriaDeModeloSchema = z.enum(['MARKETING', 'UTILITY']);

/**
 * Os status que a Meta documenta hoje — como LISTA, e não como enum fechado.
 *
 * ── Por que a resposta não os trava ──────────────────────────────────────
 *
 * O status é vocabulário da META, e a A03 §6 registra que ela acrescenta
 * estado sem avisar. Num enum fechado, cada acréscimo vira quebra de contrato
 * `/v1` — o que exigiria rótulo, ADR e `/v2` convivendo, para um campo que só
 * repassa uma palavra de terceiro.
 *
 * Este arquivo aprendeu isso caro e rápido: a PR que acrescentou `IN_REVIEW` e
 * `APPEAL_REQUESTED` — dois status que a Meta já documentava e que eu tinha
 * esquecido — foi barrada como quebra. Rotular aquilo de quebra seria mentir;
 * o certo era o campo nunca ter sido fechado.
 *
 * ── O que continua garantido ─────────────────────────────────────────────
 *
 * A API NORMALIZA antes de responder (`statusConhecido`): o que ela não conhece
 * vira `PAUSED`, o mais conservador. Então quem lê recebe, na prática, um valor
 * desta lista — sem que a lista precise ser uma promessa de contrato.
 *
 * `PENDING` e `IN_REVIEW` são o mesmo estado com dois nomes: a API devolve um e
 * a documentação lista o outro. `PAUSED` e `DISABLED` a Meta aplica sozinha
 * depois de o modelo estar no ar, por feedback negativo e baixa taxa de
 * leitura, numa escada de 3 h → 6 h → desabilitado.
 */
export const STATUS_DE_MODELO = [
  'APPROVED',
  'PENDING',
  'IN_REVIEW',
  'REJECTED',
  'PAUSED',
  'DISABLED',
  'APPEAL_REQUESTED',
] as const;

export const ModeloDeMensagemSchema = z
  .object({
    nome: z.string(),
    categoria: z.string(),
    idioma: z.string(),
    /** Texto livre de propósito — ver `STATUS_DE_MODELO`. */
    status: z.string(),
    /** O corpo como a Meta o guarda, com `{{1}}` onde entram as variáveis. */
    corpo: z.string(),
    rodape: z.string().nullable(),
    /**
     * Por que a Meta recusou, quando recusou.
     *
     * Sem isto a revenda vê "Recusado" e não tem o que fazer com a informação —
     * e o motivo mais comum (`INVALID_FORMAT`, variável sem exemplo) é
     * exatamente o que ela conseguiria corrigir sozinha.
     */
    motivo_da_recusa: z.string().nullable(),
    /** Quantas variáveis o corpo usa. A tela cobra um valor para cada. */
    variaveis: z.number().int().min(0),
  })
  .strict();

export const ModelosDeMensagemSchema = z
  .object({
    itens: z.array(ModeloDeMensagemSchema),
    stale: z.literal(false),
  })
  .strict();

/**
 * O resultado de pedir ao motor que releia os modelos da Meta.
 *
 * `mudou` é o que a tela precisa para não mentir. O motor sincroniza em
 * segundo plano e não diz quando terminou: a API espera alguns segundos pela
 * mudança no catálogo. `false` quer dizer "nada mudou nesse tempo" — que é o
 * caso de não haver modelo novo, e também o de a Meta ter demorado. A tela diz
 * as duas coisas, em vez de afirmar que trouxe algo.
 */
export const SincroniaDeModelosSchema = z
  .object({
    /** Quantos modelos o Atendimento passa a oferecer nesta caixa. */
    modelos: z.number().int().nonnegative(),
    mudou: z.boolean(),
    sincronizado_em: z.string().datetime(),
  })
  .strict();

export type SincroniaDeModelos = z.infer<typeof SincroniaDeModelosSchema>;

export const CriarModeloDeMensagemSchema = z
  .object({
    nome: NomeDeModeloSchema,
    categoria: CategoriaDeModeloSchema,
    /** `pt_BR` cobre o uso real; a Meta trata idioma como parte da identidade. */
    idioma: z.string().min(2).max(10),
    /** Teto da Meta para o corpo. */
    corpo: z.string().min(1).max(1024),
    /** Teto da Meta para o rodapé — bem menor que o do corpo, e é fácil errar. */
    rodape: z.string().max(60).optional(),
    /**
     * Um exemplo por variável do corpo, na ordem.
     *
     * A Meta RECUSA modelo com variável e sem exemplo, e esse é o motivo de
     * recusa mais comum que existe. Exigir aqui transforma uma recusa que demora
     * horas num erro de formulário que aparece na hora.
     */
    exemplos: z.array(z.string().min(1)).max(10).optional(),
  })
  .strict();

export type ModeloDeMensagem = z.infer<typeof ModeloDeMensagemSchema>;
export type ModelosDeMensagem = z.infer<typeof ModelosDeMensagemSchema>;
export type CriarModeloDeMensagem = z.infer<typeof CriarModeloDeMensagemSchema>;
export type CategoriaDeModelo = z.infer<typeof CategoriaDeModeloSchema>;
/** Os conhecidos. A resposta aceita qualquer texto; ver `STATUS_DE_MODELO`. */
export type StatusDeModelo = (typeof STATUS_DE_MODELO)[number];

/**
 * Os tipos de canal que a tela de Conexões sabe nomear (29/09/2026).
 *
 * São os que o motor de conversa tem, traduzidos: a tela diz "Web Chat", e
 * não `Channel::WebWidget`. `outro` segura o tipo que aparecer numa versão
 * nova do motor antes de a tela saber o nome dele — melhor mostrar a caixa
 * sem ícone que escondê-la.
 */
export const TIPOS_DE_CANAL = [
  'whatsapp',
  'instagram',
  'facebook',
  'web_chat',
  'api',
  'tiktok',
  'telegram',
  'email',
  'sms',
  'line',
  'outro',
] as const;

export const TipoDeCanalSchema = z.enum(TIPOS_DE_CANAL);

export const CanalConectadoSchema = z
  .object({
    /** O id da caixa no motor. */
    referencia_externa: z.string(),
    nome: z.string(),
    tipo: TipoDeCanalSchema,
    /**
     * O que identifica o canal para quem olha: o telefone do WhatsApp, o site
     * do Web Chat, o e-mail. Nulo quando o canal não tem um (API, Facebook).
     */
    detalhe: z.string().nullable(),
    /**
     * Como ligar o canal por fora (29/09/2026): o código de instalação do Web
     * Chat, que vai no site, ou o identificador do canal de API, que o outro
     * sistema usa. Nulo nos demais.
     */
    instalacao: z.string().nullable(),
  })
  .strict();

/** `POST .../chatwoot/channels/web-chat` — um chat no site da revenda. */
export const CriarWebChatSchema = z
  .object({
    nome: z.string().trim().min(2).max(60),
    /** O site onde o chat vai aparecer. */
    site: z
      .string()
      .url()
      .refine((u) => /^https?:\/\//.test(u), 'O site precisa começar com http:// ou https://.'),
    /**
     * Os campos da criação do Web Chat no motor (30/09/2026): a cor do botão e
     * da janela, e o que aparece no topo quando o visitante abre o chat.
     * Ausentes, valem o roxo da marca e os textos padrão.
     */
    cor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/, 'A cor precisa estar no formato #RRGGBB.')
      .optional(),
    titulo_de_boas_vindas: z.string().trim().max(80).optional(),
    frase_de_boas_vindas: z.string().trim().max(255).optional(),
  })
  .strict();

/** `POST .../chatwoot/channels/api` — um canal que outro sistema alimenta. */
export const CriarCanalDeApiSchema = z
  .object({
    nome: z.string().trim().min(2).max(60),
    /** Onde o motor avisa as respostas. Opcional: dá para ligar depois. */
    webhook_url: z
      .string()
      .url()
      .refine((u) => /^https:\/\//.test(u), 'O endereço de aviso precisa ser https://.')
      .optional(),
  })
  .strict();

/**
 * `GET .../chatwoot/channels` — TODOS os canais da revenda, de qualquer tipo.
 *
 * A lista de WhatsApp continua existindo, porque o cartão do número tem
 * modelos e mensagens automáticas; esta é a visão de cima, no formato da lista
 * de caixas do motor.
 */
export const CanaisConectadosSchema = z
  .object({
    itens: z.array(CanalConectadoSchema),
    stale: z.literal(false),
  })
  .strict();

/**
 * Quem atende um canal (29/09/2026): as pessoas da organização que podem
 * atender, e quais são membros daquela caixa no motor.
 */
export const AtendenteDoCanalSchema = z
  .object({
    usuario_id: z.string().uuid(),
    nome: z.string(),
    /** A foto de perfil (061), por link assinado. */
    foto_url: z.string().nullable(),
    /** Membro da caixa: a distribuição e o "Passar" consideram esta pessoa. */
    atende: z.boolean(),
  })
  .strict();

export const AtendentesDoCanalSchema = z
  .object({
    itens: z.array(AtendenteDoCanalSchema),
    /**
     * Pessoas ativas que ainda não têm acesso ao atendimento e por isso não
     * aparecem na lista. A tela diz quantas, para ninguém procurar um nome
     * que não está ali.
     */
    sem_acesso_ao_atendimento: z.number().int().nonnegative(),
    stale: z.literal(false),
  })
  .strict();

/**
 * `PUT .../channels/{id}/atendentes` — a lista INTEIRA de quem atende.
 *
 * Ao menos uma pessoa: uma caixa sem membros recebe as conversas e não as
 * entrega a ninguém.
 */
export const DefinirAtendentesSchema = z
  .object({
    usuario_ids: z.array(z.string().uuid()).min(1).max(500),
  })
  .strict();

export const CanaisDeWhatsAppSchema = z
  .object({
    itens: z.array(CanalDeWhatsAppSchema),
    /**
     * Lido do motor a cada chamada — não há projeção, logo não há defasagem.
     *
     * `false` literal em vez de campo ausente: a tela trata frescor do mesmo
     * jeito em todo bloco, e um bloco sem o campo viraria exceção no código
     * dela.
     */
    stale: z.literal(false),
  })
  .strict();

export const ConfigurarIntegracaoSchema = z
  .object({
    base_url: z.string().url(),
    conta_externa: z.string().min(1),
    /**
     * A única vez em que o segredo atravessa o sistema.
     *
     * Opcional na atualização: quem só corrige a URL não redigita a credencial
     * — e obrigá-lo a isso faria a pessoa colar de onde ela estava guardada
     * errado.
     */
    credencial: z.string().min(1).nullish(),
    /**
     * Gera um `hook_key` novo, invalidando o anterior.
     *
     * Padrão desligado de propósito: rotacionar quebra a URL já configurada do
     * lado do provedor, e webhook que para de chegar sem ninguém ter mexido é
     * uma tarde perdida.
     */
    rotacionar_hook_key: z.boolean().optional(),
  })
  .describe('Cadastro ou atualização de conexão de engine');

export const ResultadoDaConfiguracaoSchema = z
  .object({
    conexao: IntegracaoSchema,
    /**
     * Presente **só** nesta resposta, e nunca mais.
     *
     * O banco guarda o SHA-256 — nem nós conseguimos exibi-lo de novo. A tela
     * precisa dizer isso antes de a pessoa fechar a janela.
     */
    hook_key: z.string().optional(),
  })
  .describe('Conexão salva; `hook_key` só quando foi gerado agora');

/* ── O catálogo de plataformas (D11 + E02t) ─────────────────────────────── */

/**
 * Um campo que a plataforma exige, como a tela precisa vê-lo.
 *
 * ── O que este schema NÃO tem, e é o ponto ────────────────────────────────
 *
 * Valor. Nem o preenchido, nem o padrão. Isto descreve a PERGUNTA — "o portal
 * pede um código de anunciante, é texto, é obrigatório, e você o encontra
 * assim" — e nunca a resposta. É por isso que ele pode ser lido por quem
 * apenas visualiza integrações: não há segredo em dizer o que um portal exige.
 */
export const CampoExigidoSchema = z
  .object({
    chave: z.string(),
    rotulo: z.string(),
    tipo: z.enum(['texto', 'segredo', 'url', 'numero', 'escolha']),
    /**
     * `aplicacao` = preenchido uma vez, pela instalação (o `client_id` do nosso
     * aplicativo no portal). `revenda` = por conexão.
     *
     * A tela usa isto para não pedir o segredo do integrador a cada revenda que
     * for conectar — que é como esse segredo começaria a circular por e-mail.
     */
    escopo: z.enum(['aplicacao', 'revenda']),
    obrigatorio: z.boolean(),
    /** Onde a revenda acha este valor. É o campo que evita o chamado. */
    onde_encontrar: z.string(),
    exemplo: z.string().optional(),
    opcoes: z.array(z.string()).optional(),
  })
  .describe('Um campo que a plataforma exige para conectar');

export const PlataformaSchema = z
  .object({
    provider: z.string(),
    rotulo: z.string(),
    /**
     * `fichado` — sabemos o que o portal exige; **não há código** que fale com
     * ele. `implementado` — existe conector. `homologado` — passou por teste de
     * contrato, e é o único que deveria rodar em produção.
     *
     * A distinção é o que impede o catálogo de parecer anunciar integrações
     * prontas que ainda não existem.
     */
    situacao: z.enum(['fichado', 'implementado', 'homologado']),
    capacidades: z.array(z.string()),
    direcao: z.enum(['entrada', 'saida', 'bidirecional']),
    autenticacao: z.enum(['oauth2', 'api_key', 'hmac', 'basic', 'sem_credencial']),
    ingresso: z.enum(['webhook', 'poller', 'ambos']),
    /** A chave que liga esta plataforma para uma revenda (princípio 08). */
    feature_flag: z.string(),
    /** Se ESTA revenda já tem a flag ligada. */
    ativa_no_tenant: z.boolean(),
    configuracao: z.array(CampoExigidoSchema),
  })
  .describe('Plataforma que o produto conhece, e o que ela exige para conectar');

export const CatalogoDePlataformasSchema = z.object({ itens: z.array(PlataformaSchema) });

export const ResultadoDaLigacaoSchema = z
  .object({
    connection_id: z.string().uuid(),
    /**
     * Contém o `hook_key`, que é o segredo do regime `url_secreta`. Volta UMA
     * vez, nesta resposta: o banco guarda só o SHA-256, e nem o servidor
     * consegue exibi-la de novo.
     */
    webhook_url: z.string(),
    regime: z.literal('hmac'),
  })
  .describe('Webhook do Chatwoot registrado; a conexão sobe para assinatura HMAC');

/* ── Canal de WhatsApp (G02) ─────────────────────────────────────────────── */

export const ConectarWhatsAppSchema = z
  .object({
    /** E.164, com o `+` e o código do país. */
    telefone: z.string().regex(/^\+[1-9]\d{7,14}$/, 'formato internacional, como +5511988887777'),
    /** Como a caixa aparece para o vendedor. */
    nome: z.string().min(2).max(60),
    /**
     * `phone_number_id` da Meta — o identificador do número, e não o número.
     *
     * Os dois existem e são diferentes; confundi-los é o erro mais comum de
     * quem configura pela primeira vez, e o portal da Meta os mostra lado a
     * lado.
     */
    id_do_numero: z.string().min(1),
    /** `business_account_id` — a conta comercial (WABA) a que o número pertence. */
    id_da_conta_comercial: z.string().min(1),
    /**
     * O access token da Meta.
     *
     * Atravessa este sistema uma vez e vai para o Chatwoot, que precisa dele em
     * claro a cada mensagem. Não fica guardado do nosso lado — ver
     * `canal-whatsapp.ts`.
     */
    token: z.string().min(1),
  })
  .describe('Conectar um número de WhatsApp Cloud à organização');

export const CanalCriadoSchema = z
  .object({
    inbox_id: z.number().int().positive(),
    nome: z.string(),
    telefone: z.string(),
  })
  .describe('Caixa criada; é por ela que a conversa passa a chegar');

/* ── Facebook e Instagram, pelo OAuth da Meta (G02) ──────────────────────── */

export const AutorizacaoDaMetaSchema = z
  .object({
    /** Para onde mandar a revenda. A tela da Meta mostra o nome do NOSSO app. */
    url: z.string().url(),
  })
  .describe('Início da autorização na Meta');

export const PaginaDisponivelSchema = z.object({
  id: z.string(),
  nome: z.string(),
  /** Se há conta Instagram Business ligada — ela é vinculada junto. */
  tem_instagram: z.boolean(),
  /** Se esta página já está conectada nesta revenda. */
  ja_conectada: z.boolean(),
});

export const PaginasDaMetaSchema = z
  .object({
    /**
     * Referência da autorização, para o passo de conectar.
     *
     * NÃO é o token: é o endereço dele no cofre. O token do usuário abre todas
     * as páginas que a pessoa administra, e não pode circular numa resposta que
     * o navegador guarda no histórico.
     */
    autorizacao: z.string(),
    paginas: z.array(PaginaDisponivelSchema),
  })
  .describe('Páginas que a autorização alcança, para a organização escolher');

export const RetornoDaMetaSchema = z
  .object({
    /** O `code` que a Meta devolveu no redirecionamento. */
    code: z.string().min(1),
    /** O `state` assinado que saiu daqui. É ele que diz de quem é a autorização. */
    state: z.string().min(1),
  })
  .describe('O que a Meta devolve no retorno do consentimento');

export const ConectarWhatsAppPorSignupSchema = z
  .object({
    /**
     * O `code` que o popup da Meta devolveu.
     *
     * De curta duração e de uso único. Quem o tem pode trocá-lo por um token —
     * por isso ele não entra em log nem em auditoria.
     */
    code: z.string().min(1),
    /** `business_id` — o Business Manager dono da conta. */
    business_id: z.string().min(1),
    /** `waba_id` — a conta de WhatsApp Business. */
    waba_id: z.string().min(1),
    /**
     * `phone_number_id`. Opcional: o motor o descobre pela WABA.
     *
     * Vale mandar quando o popup o entrega — a conta pode ter mais de um número,
     * e sem ele o motor escolhe.
     */
    phone_number_id: z.string().min(1).optional(),
  })
  .describe('Conectar WhatsApp pelo Embedded Signup da Meta');

export const CanalPorSignupSchema = z
  .object({
    inbox_id: z.number().int().positive(),
    nome: z.string(),
    waba_id: z.string(),
  })
  .describe('Número conectado; o motor já o inscreveu no webhook');

export const ConectarPaginaSchema = z
  .object({
    autorizacao: z.string().min(1),
    page_id: z.string().min(1),
    /** Como a caixa aparece para o vendedor. */
    nome: z.string().min(2).max(60),
  })
  .describe('Conectar uma página do Facebook, com o Instagram ligado a ela');

export const CanalDaMetaSchema = z
  .object({
    inbox_id: z.number().int().positive(),
    nome: z.string(),
    page_id: z.string(),
    /** Se o Instagram da página entrou junto. */
    com_instagram: z.boolean(),
  })
  .describe('Página conectada; é por ela que a conversa passa a chegar');

export type Integracao = z.infer<typeof IntegracaoSchema>;
export type AutorizacaoDaMeta = z.infer<typeof AutorizacaoDaMetaSchema>;
export type PaginaDisponivel = z.infer<typeof PaginaDisponivelSchema>;
export type PaginasDaMeta = z.infer<typeof PaginasDaMetaSchema>;
export type ConectarPagina = z.infer<typeof ConectarPaginaSchema>;
export type ConectarWhatsAppPorSignup = z.infer<typeof ConectarWhatsAppPorSignupSchema>;
export type CanalPorSignup = z.infer<typeof CanalPorSignupSchema>;
export type RetornoDaMeta = z.infer<typeof RetornoDaMetaSchema>;
export type CanalDaMeta = z.infer<typeof CanalDaMetaSchema>;
export type ConectarWhatsApp = z.infer<typeof ConectarWhatsAppSchema>;
export type CanalCriado = z.infer<typeof CanalCriadoSchema>;
export type ResultadoDaLigacao = z.infer<typeof ResultadoDaLigacaoSchema>;
export type CampoExigido = z.infer<typeof CampoExigidoSchema>;
export type Plataforma = z.infer<typeof PlataformaSchema>;
export type CatalogoDePlataformas = z.infer<typeof CatalogoDePlataformasSchema>;
export type ListaDeIntegracoes = z.infer<typeof ListaDeIntegracoesSchema>;
export type ConfigurarIntegracao = z.infer<typeof ConfigurarIntegracaoSchema>;
export type ResultadoDaConfiguracao = z.infer<typeof ResultadoDaConfiguracaoSchema>;

export type CanalDeWhatsApp = z.infer<typeof CanalDeWhatsAppSchema>;
export type TipoDeCanal = z.infer<typeof TipoDeCanalSchema>;
export type AtendenteDoCanal = z.infer<typeof AtendenteDoCanalSchema>;
export type AtendentesDoCanal = z.infer<typeof AtendentesDoCanalSchema>;
export type DefinirAtendentes = z.infer<typeof DefinirAtendentesSchema>;
export type CanalConectado = z.infer<typeof CanalConectadoSchema>;
export type CanaisConectados = z.infer<typeof CanaisConectadosSchema>;
export type CriarWebChat = z.infer<typeof CriarWebChatSchema>;
export type CriarCanalDeApi = z.infer<typeof CriarCanalDeApiSchema>;
